import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import { useTheme } from './ThemeProvider';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Sun, Moon, Ship, TrendingUp, Shield, Package, Activity, Link as LinkIcon, ShieldCheck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import axios from 'axios';
import { usePermissions } from './PermissionsProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { role, hasModuleAccess, loading: permissionsLoading } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  const allNavigation = [
    { name: 'Dashboard', path: '/dashboard', icon: TrendingUp, module: 'dashboard' },
    { name: 'Trade Analytics', path: '/trade-analytics', icon: Package, module: 'trade_analytics' },
    { name: 'Insurance', path: '/insurance', icon: Shield, module: 'insurance' },
    { name: 'Marketplace', path: '/marketplace', icon: Ship, module: 'marketplace' },
    { name: 'Usage & Billing', path: '/usage', icon: Activity, module: null },
    { name: 'Integrations', path: '/integrations', icon: LinkIcon, module: 'integrations' },
  ];
  
  // Filter navigation based on permissions
  const navigation = allNavigation.filter(item => {
    if (!item.module) return true; // Always show items without module (like Usage & Billing)
    return hasModuleAccess(item.module, 'view');
  });
  
  // Add Admin link only for super admins
  if (role === 'super_admin') {
    navigation.push({ name: 'Admin', path: '/admin', icon: ShieldCheck, module: null });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 w-full border-b bg-[hsl(var(--card))] backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--card))]/95">
        <div className="flex h-14 items-center px-4 md:px-6">
          {/* Logo and Menu Toggle */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <Ship className="h-6 w-6 text-[hsl(var(--accent))]" />
              <span className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                Maritime Pulse
              </span>
            </Link>
          </div>

          {/* Right side actions */}
          <div className="ml-auto flex items-center gap-4">
            {/* Theme Toggle */}
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4" />
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                data-testid="theme-toggle"
              />
              <Moon className="h-4 w-4" />
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="user-menu">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name}</p>
                    <p className="text-xs leading-none text-[hsl(var(--muted-foreground))]">
                      {user?.email}
                    </p>
                    {role && (
                      <Badge 
                        variant="outline" 
                        className={`text-xs mt-2 ${
                          role === 'super_admin' ? 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]' :
                          role === 'company_admin' ? 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border-[hsl(var(--accent))]' :
                          role === 'manager' ? 'bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]' :
                          'bg-[hsl(var(--muted))]/10'
                        }`}
                      >
                        {role.replace('_', ' ').toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed left-0 top-14 z-40 h-[calc(100vh-3.5rem)] border-r bg-[hsl(var(--card))] transition-transform duration-200
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:sticky md:translate-x-0
            ${sidebarOpen ? 'w-64' : 'w-16'}
          `}
          data-testid="sidebar"
        >
          <nav className="flex flex-col gap-2 p-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors
                    ${isActive(item.path)
                      ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                      : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]'
                    }
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="container max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};